import {
  isQuestionVisible,
  isQuestionRequired,
  isAnswered,
} from './conditionEvaluator';

/**
 * Shared fill-time gating + stale-answer logic (ULLR-150 Stage 5).
 *
 * ONE definition of "does this question block page advance?" used by BOTH the
 * real fill flow (`QuestionSet.shouldEnableNext`) and the builder's preview
 * modal, so preview behavior stays fill behavior by construction.
 *
 * Gating rule (the Stage 4→5 reconcile, fill semantics win):
 * - markdown never gates (it has no answer);
 * - dateTime never gates — fill's existing behavior: a default timestamp is
 *   applied on submit (`setDefaultValuesForDateTimeQuestions`);
 * - hidden questions never gate (and are never required);
 * - a required question gates on `isComplete() === 1` — minAnswers still
 *   means minAnswers — EXCEPT when the question is minAnswers-based (select
 *   family / images) and its `minAnswers` is 0: then it gates on ≥1 answer.
 *   That closes the conditionally-required hole (a requiredCondition forces
 *   static required:false, which zeroes minAnswers, making isComplete()
 *   permanently 0) and also unsticks legacy data authored with required:true
 *   + minAnswers:0, which could previously never satisfy the gate.
 */

type CompletedFormLike = { questionSets: Array<{ questions: any[] }> };

/** Does the current answer satisfy a REQUIRED question's gate? */
export const answerSatisfiesRequirement = (question: any): boolean => {
  if (question.isComplete() === 1) {
    return true;
  }
  // minAnswers-based types (selects/images) with minAnswers 0 gate on ≥1 answer
  if (Object.prototype.hasOwnProperty.call(question, 'minAnswers') && !question.minAnswers) {
    return isAnswered(question.value);
  }
  return false;
};

/** True when the question does NOT block advancing past its page. */
export const questionAllowsAdvance = (question: any, completedForm: CompletedFormLike): boolean => {
  if (question.questionType === 'markdown') {
    return true;
  }
  if (question.questionType === 'dateTime') {
    // fill's existing behavior: dateTime always advances (defaulted on submit)
    return true;
  }
  if (!isQuestionVisible(question, completedForm)) {
    return true;
  }
  if (!isQuestionRequired(question, completedForm)) {
    return true;
  }
  return answerSatisfiesRequirement(question);
};

/**
 * Stale-answer clearing (Q4 kickoff decision): when an answer change flips a
 * dependent question's displayCondition to unmet, that dependent's value is
 * cleared — transitively, since clearing an answer can hide ITS dependents.
 *
 * Called at the answer-update choke point (FormResponseModal.updateQuestionSet
 * and the preview modal's equivalent), so it covers same-page AND cross-page
 * dependents. Cleared questions are replaced with fresh clones (value unset,
 * question object still present — the stored completedForm shape is
 * unchanged); untouched sibling objects are not mutated.
 *
 * Returns the ids of the questions whose values were cleared.
 */
export const clearHiddenStaleAnswers = (completedForm: any): string[] => {
  const clearedIds: string[] = [];
  // fixpoint: each pass can hide further dependents (chains); the pass count
  // is bounded by the question count since every pass must clear ≥1 value
  const maxPasses = completedForm.questionSets.reduce(
    (count: number, questionSet: any) => count + questionSet.questions.length, 0,
  ) + 1;

  for (let pass = 0; pass < maxPasses; pass++) {
    const toClear: string[] = [];
    for (const questionSet of completedForm.questionSets) {
      for (const question of questionSet.questions) {
        if (question.displayCondition
          && question.value !== undefined
          && !isQuestionVisible(question, completedForm)) {
          toClear.push(question.id);
        }
      }
    }
    if (!toClear.length) {
      break;
    }
    clearedIds.push(...toClear);
    completedForm.questionSets = completedForm.questionSets.map((questionSet: any) => {
      if (!questionSet.questions.some((question: any) => toClear.includes(question.id))) {
        return questionSet;
      }
      const clonedSet = questionSet.clone();
      clonedSet.questions = clonedSet.questions.map((question: any) => {
        if (!toClear.includes(question.id)) {
          return question;
        }
        const clonedQuestion = question.clone();
        clonedQuestion.value = undefined;
        return clonedQuestion;
      });
      return clonedSet;
    });
  }

  return clearedIds;
};
